import { useMemo, useState, useCallback } from 'react'
import { debounce } from 'lodash'
import {
    ScoopSelector,
    ScoopTextField,
    ScoopText,
    ScoopInfoTooltip,
    ScoopSwitch,
} from '../../../../ui'
import { useExplorer } from '../../../../../context/explorer'
import styles from './ConfigTab.module.scss'

// KPI comparison type options
const KPI_COMPARE_OPTIONS = [
    { label: 'Percentage', value: 'percentage' },
    { label: 'Number', value: 'number' },
    { label: 'None', value: 'none' },
]

/**
 * ChartSettings component for configuring chart type, stacking, and KPI settings.
 *
 * Uses ExplorerContext via useExplorer().
 * Uses setSeriesType, setStacked, setChartTitle, setSeriesTypeForMetric,
 * setKpiCompareType, and setKpiCompareTarget actions.
 */
export const ChartSettings = () => {
    // Get state and actions from context
    const {
        viewConfig,
        dataConfig,
        queryConfig,
        summaryData,
        setSeriesType,
        setStacked,
        setChartTitle,
        setSeriesTypeForMetric,
        setKpiCompareType,
        setKpiCompareTarget,
    } = useExplorer()

    // Local state for KPI target (debounced)
    const [kpiTargetValue, setKpiTargetValue] = useState(viewConfig.kpiCompareTarget || '')

    // Debounced KPI target update
    const debouncedSetKpiTarget = useCallback(
        debounce((value) => {
            setKpiCompareTarget(value)
        }, 500),
        [setKpiCompareTarget]
    )

    // Handle chart type change
    const handleChartTypeChange = (e) => {
        setSeriesType(e.target.value)
    }

    // Handle per-metric chart type change
    const handleMetricChartTypeChange = (metricName, e) => {
        setSeriesTypeForMetric(metricName, e.target.value)
    }

    // Handle stacking toggle
    const handleStackingChange = () => {
        setStacked(!viewConfig.stacked)
    }

    // Handle chart title change
    const handleTitleChange = (e) => {
        setChartTitle(e.target.value)
    }

    // Handle KPI compare type change
    const handleKpiCompareTypeChange = (e) => {
        setKpiCompareType(e.target.value)
    }

    // Handle KPI target value change
    const handleKpiTargetChange = (e) => {
        const value = e.target.value.replace(/\D/g, '') // Only allow numbers
        setKpiTargetValue(value)
        debouncedSetKpiTarget(value)
    }

    // Check if stacking is applicable (only for column and bar charts)
    const isStackingApplicable = ['column', 'bar'].includes(viewConfig.seriesType)

    // Check if we're in KPI view
    const isKpiView = viewConfig.view === 'kpi'

    // Check if we're in chart view
    const isChartView = viewConfig.view === 'chart'

    // Check if we have multiple metrics (for per-metric chart type)
    const metrics = summaryData?.metrics || []
    const hasMultipleMetrics = metrics.length > 1

    // Build chart type options based on current data configuration
    const chartTypeOptions = useMemo(() => {
        const options = [
            { label: 'Column', value: 'column' },
            { label: 'Line', value: 'line' },
            { label: 'Area', value: 'area' },
            { label: 'Bar', value: 'bar' },
        ]

        // Add conditional options based on data configuration
        if (dataConfig.selectedItems?.length <= 1) {
            options.push({ label: 'Pie', value: 'pie' })
            options.push({ label: 'Donut', value: 'donut' })

            if (dataConfig.drillAttribute && dataConfig.categoryAxis !== 'Time') {
                options.push({ label: 'Heatmap', value: 'heatmap' })
            }
        }

        options.push({ label: 'Pictorial', value: 'pictorialBar' })
        options.push({ label: 'Gauge', value: 'gauge' })
        options.push({ label: 'Radial Bar', value: 'radialBar' })

        if (dataConfig.selectedItems?.length === 2) {
            options.push({ label: 'Scatter', value: 'scatter' })
        }

        if (dataConfig.worksheetID) {
            options.push({ label: 'Waterfall', value: 'waterfall' })
        }

        return options
    }, [
        dataConfig.selectedItems,
        dataConfig.drillAttribute,
        dataConfig.categoryAxis,
        dataConfig.worksheetID,
    ])

    return (
        <div className={styles.sectionContainer}>
            {/* Title field - shown for chart and KPI views */}
            <ScoopTextField
                value={viewConfig.chartTitle || ''}
                onChange={handleTitleChange}
                placeholder="Enter title..."
                label={isKpiView ? 'KPI title' : 'Chart title'}
            />

            {/* Chart Type - only for chart view */}
            {isChartView && (
                <ScoopSelector
                    value={viewConfig.seriesType}
                    onChange={handleChartTypeChange}
                    label="Chart Type"
                    options={chartTypeOptions}
                />
            )}

            {/* Per-metric Chart Type - for chart view with multiple metrics */}
            {isChartView && hasMultipleMetrics && (
                <>
                    {metrics.map((metric) => (
                        <ScoopSelector
                            key={metric.name}
                            value={
                                viewConfig.seriesTypeMap?.get?.(metric.name) ||
                                viewConfig.seriesType
                            }
                            onChange={(e) => handleMetricChartTypeChange(metric.name, e)}
                            label={`${metric.name} Chart Type`}
                            options={chartTypeOptions}
                        />
                    ))}
                </>
            )}

            {/* Stacking - only for chart view with bar/column charts */}
            {isChartView && (
                <div className={styles.stackingRow}>
                    <div className={styles.stackingLabel}>
                        <ScoopText className={styles.stackingText}>Stacking</ScoopText>
                        <ScoopInfoTooltip
                            title={'Stacking is only available for bar and column charts.'}
                        />
                    </div>
                    <ScoopSwitch
                        name={'stacking-switch'}
                        checked={viewConfig.stacked}
                        onChange={handleStackingChange}
                        disabled={!isStackingApplicable || dataConfig.selectedItems?.length === 0}
                    />
                </div>
            )}

            {/* KPI Compare Type - only for KPI view */}
            {isKpiView && (
                <ScoopSelector
                    value={viewConfig.kpiCompareType || 'percentage'}
                    onChange={handleKpiCompareTypeChange}
                    label={`${!queryConfig.tableTimeSeries ? 'Target' : 'Prior period'} comparison`}
                    options={KPI_COMPARE_OPTIONS}
                />
            )}

            {/* KPI Target Value - only for KPI view without time series */}
            {isKpiView && !queryConfig.tableTimeSeries && (
                <ScoopTextField
                    value={kpiTargetValue}
                    onChange={handleKpiTargetChange}
                    placeholder="Enter target value..."
                    label="Target value"
                />
            )}
        </div>
    )
}
